import { createAsyncThunk } from "@reduxjs/toolkit"
import { AxiosProgressEvent } from "axios"
import oktaAuthClient, {
  clearLocalStorage,
  getAccessToken,
} from "@/core/config/okta.config"
import * as v2Apis from "../../v2/v2.service"

import {
  downloadPhotographFiles,
  fetchFileList,
  getS3FileUrl,
  updatePhotoPositionAPI,
  uploadPhoto,
  uploadPhotographFiles,
  uploadFiles,
  uploadPhotoJSONAPI,
  uploadThumbnails,
  downloadPhotoJSONAPI,
  uploadPhotoRotateJSONAPI,
  downloadPhotoRotateJSONAPI,
  uploadOnePhotoFile,
  deletePhotographFile,
} from "./photograph.service"
import {
  fetchFileParams,
  fileDownloadParams,
  IPhotosDownloadParams,
  fileUploadParams,
  updatePhotoPositionParams,
  uploadPhotoTypeParams,
  downloadPhotoTypeParams,
  IUploadFilesCommonParams,
  IPhotosDeleteParams,
  IAIRequetParams,
} from "./photograph.type"

export const uploadPhotographsInv_1 = createAsyncThunk(
  "PhotosService/uploadPhotographs",
  async (
    {
      patientId,
      caseId,
      formData,
      fileName,
      onFileProcesscallback,
    }: fileUploadParams,
    { rejectWithValue, getState },
  ) => {
    const orgId = getState().userService.user.current_orgId
    try {
      return await uploadOnePhotoFile(
        orgId,
        patientId,
        caseId,
        fileName,
        formData,
        onFileProcesscallback,
      )
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

interface UploadFormDatas {
  formData: FormData
  fileName: string
}

export const uploadCaseFiles = createAsyncThunk(
  "PhotosService/uploadCaseFiles",
  async (
    { patientId, caseId, files, callback }: IUploadFilesCommonParams,
    { rejectWithValue, getState },
  ) => {
    const orgId = getState().userService.user.current_orgId
    try {
      return uploadFiles({
        patientId,
        caseId,
        files,
        orgId,
        callback,
      })
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const uploadThumbnailv1_1 = createAsyncThunk(
  "PhotosService/uploadThumbnailArray",
  async (
    {
      patientId,
      caseId,
      files,
      callback,
      refinementIndex = 0,
    }: IUploadFilesCommonParams,
    { rejectWithValue, getState },
  ) => {
    const orgId = getState().userService.user.current_orgId
    try {
      const ret = uploadThumbnails({
        patientId,
        caseId,
        files,
        orgId,
        callback,
        refinementIndex,
      })
      return ret
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const uploadPhotographsv1_1 = createAsyncThunk(
  "PhotosService/uploadPhotographArray",
  async (
    {
      patientId,
      caseId,
      files,
      callback,
      refinementIndex = 0,
    }: IUploadFilesCommonParams,
    { rejectWithValue, getState },
  ) => {
    const orgId = getState().userService.user.current_orgId
    const ret = await uploadPhotographFiles({
      orgId,
      patientId,
      caseId,
      files,
      refinementIndex,
      callback,
    })
    return ret
  },
)

export const uploadPhotoJSON = createAsyncThunk(
  "PhotosService/uploadPhotoJSON",
  async (
    {
      patientId,
      caseId,
      jsonObj,
      callback,
      refinementIndex,
    }: uploadPhotoTypeParams,
    { rejectWithValue, getState },
  ) => {
    const orgId = getState().userService.user.current_orgId
    try {
      const blob = new Blob([JSON.stringify(jsonObj)], {
        type: "application/json",
      })
      const file = new File(
        [blob],
        `type${refinementIndex ? refinementIndex : ""}.json`,
        {
          type: "application/json",
          lastModified: Date.now(),
        },
      )
      uploadPhotoJSONAPI(
        orgId,
        patientId,
        caseId,
        file,
        refinementIndex,
        callback,
      )
      return { jsonObj }
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const downloadPhotoJSON = createAsyncThunk(
  "PhotosService/downloadPhotoJSON",
  async (
    { patientId, caseId, callback, refinementIndex }: downloadPhotoTypeParams,
    { rejectWithValue, getState },
  ) => {
    const orgId = getState().userService.user.current_orgId
    try {
      return await downloadPhotoJSONAPI(
        orgId,
        patientId,
        caseId,
        refinementIndex,
        callback,
      )
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

/**
 * Upload photo_rotate.json (decoupled from type.json).
 * All refinements share the same file — photo file names are UUIDs and
 * never collide across refinements.
 */
export const uploadPhotoRotateJSON = createAsyncThunk(
  "PhotosService/uploadPhotoRotateJSON",
  async (
    {
      patientId,
      caseId,
      jsonObj,
      callback,
    }: {
      patientId: string
      caseId: string
      jsonObj: Record<string, number>
      callback?: (progress: AxiosProgressEvent) => void
    },
    { rejectWithValue, getState },
  ) => {
    const orgId = getState().userService.user.current_orgId
    try {
      const blob = new Blob([JSON.stringify(jsonObj)], {
        type: "application/json",
      })
      const file = new File([blob], `photo_rotate.json`, {
        type: "application/json",
        lastModified: Date.now(),
      })
      await uploadPhotoRotateJSONAPI(
        orgId,
        patientId,
        caseId,
        file,
        callback,
      )
      return { jsonObj }
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

/**
 * Download photo_rotate.json (decoupled from type.json).
 */
export const downloadPhotoRotateJSON = createAsyncThunk(
  "PhotosService/downloadPhotoRotateJSON",
  async (
    { patientId, caseId, callback }: any,
    { rejectWithValue, getState },
  ) => {
    const orgId = getState().userService.user.current_orgId
    try {
      return await downloadPhotoRotateJSONAPI(
        orgId,
        patientId,
        caseId,
        callback,
      )
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const downloadPhotographs1_1 = createAsyncThunk<
  Record<string, File>,
  IPhotosDownloadParams
>(
  "PhotosService/downloadPhotographs1_1",
  async (
    { patientId, caseId, fileNames, refinementIndex = 0 },
    { rejectWithValue, getState },
  ) => {
    const checkFileType = (file: File): File => {
      let ret: File
      const types = [".png", ".jpg", ".jpeg", ".bmp"]
      if (file.type === "") {
        let postfix
        let type = ""
        for (const it of types) {
          if (file.name.includes(it)) {
            postfix = it
            break
          }
        }
        switch (postfix) {
          case ".png":
            type = "image/png"
            break
          case ".jpg":
          case ".jpeg":
            type = "image/jpeg"
            break
          case ".bmp":
            type = "image/bmp"
            break
          default:
            type = ""
        }

        ret = new File([file], file.name, { type })
      } else {
        ret = file
      }

      return ret
    }

    const downloadPhotosOrGetFromCache = async (filenames: string[]) => {
      let needDownloadFilenames = []
      const retFileList: Record<string, File> = {}
      const photosCache = (getState() as any).PhotosService.photosCache
      const orgId = (getState() as any).userService.user.current_orgId
      console.log("<downloadOrGetFromCache:>", filenames)
      // find out what needs download?
      const cache = photosCache[caseId]
      if (cache) {
        for (let fileName of filenames) {
          if (cache[fileName]) {
            continue
          } else {
            needDownloadFilenames.push(fileName)
          }
        }
      } else {
        needDownloadFilenames = [...filenames]
      }

      // download if need
      const needDownloadPhotos: v2Apis.IPhotoInfo[] = []
      for (const key of needDownloadFilenames) {
        needDownloadPhotos.push({
          category: "photographs",
          filename: key,
        })
      }

      let downloadFileList = {}
      if (needDownloadPhotos.length > 0) {
        const fileNames = needDownloadPhotos.map((item) => item.filename)

        // here need download from the remote
        const ret: any = await Promise.all(
          fileNames.map(async (fileName) => {
            return await downloadPhotographFiles(
              orgId,
              patientId,
              caseId,
              fileName,
              refinementIndex,
            )
          }),
        )
        //const ret = await dispatch(downloadPhotosfromS3({caseId,photos:needDownloadPhotos}))

        const downloadFiles = ret as File[]
        if (downloadFiles && downloadFiles.length > 0) {
          for (let i = 0; i < downloadFiles.length; i++) {
            const file = downloadFiles[i]
            downloadFileList[file.name] = checkFileType(file)
          }
        }
        console.log("need download photos:::", ret, downloadFileList)
        //downloadFileList = ret.payload as Record<string,{data:File,status:string}>;
      }

      // get files from cache or download result.
      // filename <-> file
      for (let name of filenames) {
        if (cache && cache[name]) {
          retFileList[name] = cache[name]
        } else if (downloadFileList && downloadFileList[name]) {
          retFileList[name] = downloadFileList[name]
        }
      }
      console.log("<download result :::>", retFileList)
      return retFileList
    }

    try {
      const ret = await downloadPhotosOrGetFromCache(fileNames)
      return ret
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const deletePhotographs = createAsyncThunk<File[], IPhotosDeleteParams>(
  "PhotosServoce/deletePhotographs",
  async ({ patientId, caseId, fileNames }, { rejectWithValue, getState }) => {
    const orgId = (getState() as any).user.current_orgId
    try {
      const ret = Promise.all(
        fileNames.map(async (fileName) => {
          return await deletePhotographFile(orgId, patientId, caseId, fileName)
        }),
      )
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const downloadPhotographs = createAsyncThunk<any, fileDownloadParams>(
  "PhotosService/downloadPhotographs",
  async ({ patientId, caseId, fileName }, { rejectWithValue, getState }) => {
    const orgId = (getState() as any).userService.user.current_orgId
    try {
      return await downloadPhotographFiles(orgId, patientId, caseId, fileName)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchFilesList = createAsyncThunk<any, fetchFileParams>(
  "PhotosService/fetchFileList",
  async ({ patientId, caseId }, { rejectWithValue, getState }) => {
    const orgId = (getState() as any).userService.user.current_orgId
    try {
      return await fetchFileList(orgId, patientId, caseId)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const updatePhotoPosition = createAsyncThunk<
  any,
  updatePhotoPositionParams
>(
  "PhotosService/updatePhotoPosition",
  async ({ patientId, caseId, payload }, { rejectWithValue, getState }) => {
    const orgId = (getState() as any).userService.user.current_orgId
    try {
      return await updatePhotoPositionAPI(orgId, patientId, caseId, payload)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const getS3FileUrlAPI = createAsyncThunk<any, { caseId; casePhoto }>(
  "PhotosService/getS3FileUrl",
  async ({ caseId, casePhoto }, { rejectWithValue }) => {
    try {
      return await getS3FileUrl({ caseId, casePhoto })
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const uploadPhotographsInv_2 = createAsyncThunk<any, { url; file }>(
  "PhotosService/uploadPhoto",
  async ({ url, file }, { rejectWithValue }) => {
    try {
      console.log("upload:::>>>>", file, url)
      return await uploadPhoto({ url, file })
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

/**
 * Request AI calculation for photo types
 */
let activeWs: WebSocket | null = null
let connecting = false

export const postMessageToWebsocket = createAsyncThunk<
  Object,
  IAIRequetParams
>(
  "PhotosService/postMessageToWebsocket",
  async (
    { orgId, patientId, caseId, photosBase64Info },
    { rejectWithValue },
  ) => {
    const { VITE_WEBSOCKET_URL } = import.meta.env

    const MAX_RETRY = 3
    const HEARTBEAT_TIMEOUT = 30000

    let retryCount = 0
    let heartbeatTimeout: any
    let isFinished = false

    let resolvePromise!: (v: any) => void
    let rejectPromise!: (e: any) => void

    const cleanup = () => {
      if (heartbeatTimeout) clearTimeout(heartbeatTimeout)
      if (activeWs) {
        activeWs.onopen = null
        activeWs.onmessage = null
        activeWs.onerror = null
        activeWs.onclose = null
        activeWs.close()
        activeWs = null
      }
      connecting = false
    }

    const fail = (err: any) => {
      if (isFinished) return
      isFinished = true
      cleanup()
      rejectPromise(err)
    }

    const succeed = (data: any) => {
      if (isFinished) return
      isFinished = true
      cleanup()
      resolvePromise(data)
    }

    const retry = () => {
      if (retryCount >= MAX_RETRY) {
        fail("WebSocket reconnect failed after max retries")
        return
      }
      retryCount++
      console.warn(`Retry websocket ${retryCount}/${MAX_RETRY}`)
      setTimeout(connect, 1000)
    }

    const startHeartbeat = () => {
      heartbeatTimeout = setTimeout(() => {
        console.warn("Heartbeat timeout")
        retry()
      }, HEARTBEAT_TIMEOUT)
    }

    const resetHeartbeat = () => {
      clearTimeout(heartbeatTimeout)
      startHeartbeat()
    }

    const redirectToLogin = async () => {
      await oktaAuthClient.tokenManager.clear()
      clearLocalStorage()
      window.location.assign(`${window.location.origin}/auth/login`)
    }

    const getFreshAccessToken = async () => {
      try {
        const renewedTokens = await oktaAuthClient.token.renewTokens()
        await oktaAuthClient.tokenManager.setTokens(renewedTokens)
        return getAccessToken()
      } catch (err) {
        console.warn("[Okta] token renew failed; redirecting to login", err)
        await redirectToLogin()
        throw err
      }
    }

    const connect = async () => {
      if (connecting || isFinished) return
      connecting = true

      try {
        cleanup()
        const token = await getFreshAccessToken()

        if (!token) {
          await redirectToLogin()
          fail("Missing Okta access token")
          return
        }

        activeWs = new WebSocket(
          `${VITE_WEBSOCKET_URL}/patient-mgmt/v1/org/${orgId}/patients/${patientId}/txplans/${caseId}/ai-clinical-photo`,
          token,
        )
        startHeartbeat()

        const sendContext = JSON.stringify({
          type: "inference",
          body: {
            case_id: Number(caseId),
            token: "",
            algorithm: "photo3",
            input: { ...photosBase64Info },
          },
        })

        activeWs.onopen = () => {
          connecting = false
          retryCount = 0
          activeWs!.send(sendContext)
        }

        activeWs.onmessage = ({ data }) => {
          resetHeartbeat()
          try {
            const {
              body: { result, output },
            } = JSON.parse(data)

            if (result !== 1) return

            succeed(output)
          } catch (err) {
            fail(err)
          }
        }

        activeWs.onerror = () => retry()

        activeWs.onclose = (e) => {
          if (!isFinished && e.code === 1006) retry()
        }
      } catch (err) {
        retry()
      }
    }

    return new Promise((resolve, reject) => {
      resolvePromise = resolve
      rejectPromise = reject
      connect()
    }).catch((err) => rejectWithValue(err))
  },
)


export const uploadPhotosToS3 = createAsyncThunk(
  "utils/uploadPhotosToS3",
  async (
    params: { caseId; files?: Record<string, File> },
    { rejectWithValue, getState },
  ) => {
    try {
      const response = await v2Apis.uploadFilesToS3(
        params.caseId,
        undefined,
        params.files,
      )
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const downloadPhotosfromS3 = createAsyncThunk<
  Record<string, string | Blob>,
  { caseId: string; photos: v2Apis.IPhotoInfo[] }
>("PhotosService/downloadPhotosfromS3", async (params, { rejectWithValue }) => {
  try {
    const response = await v2Apis.requestS3Url({
      caseId: params.caseId,
      urlType: "download",
      casePhoto: params.photos,
    })
    if (response.code === 0 && response.msg === "success") {
      const respData = response.data
      console.log("respdata:::", respData)
      const result: Record<string, string | Blob | File> = {}
      let ret: Promise<File | null>[] = []
      for (let index = 0; index < respData.casePhoto.length; index++) {
        ret.push(
          new Promise(async (resolve, reject) => {
            const s3urlres = respData.casePhoto[index]
            let resRet = await v2Apis.downloadS3Resource(s3urlres)
            if (resRet instanceof Error) {
              resolve(null)
            } else {
              if (resRet.data instanceof Blob) {
                resRet = new File([resRet.data], s3urlres.destFilename)
              } else {
                // json obj
                resRet = resRet.data
              }
              resolve(resRet)
              result[s3urlres.destFilename] = resRet
            }
          }),
        )
      }

      await Promise.all(ret)

      return result
    }
  } catch (err) {
    return rejectWithValue(err)
  }
})
